/**
 * The prefill affordance.
 *
 * A prefilled field has to be visually distinct from one the technician filled in — otherwise the
 * confirmation signal is worthless, because nobody can tell what they actually asserted. Three
 * states, three appearances:
 *
 *  - **From the record** — value came from Salesforce, untouched. Shows the source.
 *  - **Confirmed** — affirmed. Still marked as record-sourced, because a confirmation is not the
 *    same evidential weight as a fresh observation.
 *  - **Corrected** — the technician changed it. Shows what the record said, struck through, because
 *    that disagreement is the most valuable byte in the submission and hiding it wastes it.
 *
 * DISPLAY-ONLY BY DESIGN (field feedback 2). This badge used to carry a per-field "This is right"
 * confirm button, which meant a form with a dozen prefills asked the same question a dozen times.
 * Confirmation now happens in ONE place — the review step lists every record-sourced value and the
 * technician flags only what is wrong there (`ReviewStep.tsx`). The badge keeps exactly one action:
 * putting the record's value back after an override, because that is a *correction* affordance tied
 * to the field being edited, not a confirmation ask.
 */

import type { Answer, FieldEntry, ReferenceValue } from '../types.ts';

function render(value: Answer): string {
  if (value === null) return '—';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (typeof value[0] === 'string') return (value as readonly string[]).join(', ');
    return `${value.length} item(s)`;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object' && 'serial' in value) return value.serial;
  return String(value);
}

export interface PrefillBadgeProps {
  readonly entry: FieldEntry | undefined;
  readonly onRevert: () => void;
}

export function PrefillBadge({ entry, onRevert }: PrefillBadgeProps): JSX.Element | null {
  if (!entry?.prefill) return null;

  if (entry.provenance === 'override') {
    return (
      <p className="fx-prov fx-prov-override" data-provenance="override">
        <span className="fx-prov-tag">Corrected</span> the record said{' '}
        <s>{render(entry.prefill.originalValue)}</s>
        {entry.prefill.field !== undefined && (
          <span className="fx-prov-src">
            {' '}
            ({entry.prefill.object}.{entry.prefill.field})
          </span>
        )}{' '}
        <button type="button" className="fx-link" onClick={onRevert}>
          Put the record’s value back
        </button>
      </p>
    );
  }

  if (entry.provenance === 'auto') {
    return (
      <p className="fx-prov fx-prov-auto" data-provenance="auto">
        <span className="fx-prov-tag">Captured automatically</span> {entry.prefill.label}
      </p>
    );
  }

  const confirmed = entry.confirmedAt !== undefined;
  return (
    <p
      className={confirmed ? 'fx-prov fx-prov-confirmed' : 'fx-prov fx-prov-prefill'}
      data-provenance={confirmed ? 'prefill-confirmed' : 'prefill'}
    >
      <span className="fx-prov-tag">{confirmed ? 'Confirmed from the record' : 'From the record'}</span>{' '}
      {entry.prefill.label}
      {entry.prefill.field !== undefined && (
        <span className="fx-prov-src">
          {' '}
          ({entry.prefill.object}.{entry.prefill.field})
        </span>
      )}
    </p>
  );
}

export interface ReferenceNoteProps {
  readonly reference: ReferenceValue | undefined;
}

/**
 * A value the record holds that is explicitly *not* the answer.
 *
 * Kept visually separate from the prefill badge so it can never read as "already filled in".
 */
export function ReferenceNote({ reference }: ReferenceNoteProps): JSX.Element | null {
  if (!reference) return null;
  return (
    <p className="fx-prov fx-prov-reference" data-provenance="reference">
      <span className="fx-prov-tag">On the record, unverified</span> <code>{render(reference.value)}</code>
      {reference.field !== undefined && (
        <span className="fx-prov-src">
          {' '}
          ({reference.object}.{reference.field})
        </span>
      )}
      <span className="fx-prov-why"> — check it rather than trusting it. This is not your answer.</span>
    </p>
  );
}

export { render as renderAnswer };
